import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { UserButton, useUser } from "@clerk/nextjs";
import { useAuth } from "../../contexts/AuthContext";
import { useSearch } from "../hooks/useSearch";
import Link from "next/link";
import Image from "next/image";
import {
  FiMenu,
  FiX,
  FiSearch,
  FiUser,
  FiList,
  FiInfo,
  FiPhone,
  FiChevronDown,
  FiChevronRight,
  FiLogIn,
  FiUserPlus,
  FiMapPin,
  FiHeart,
  FiHome,
  FiSettings,
  FiSliders,
  FiMessageSquare,
} from "react-icons/fi";

export default function Header() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useUser();
  const { isAdmin, isAgent } = useAuth();
  const {
    searchParams,
    updateSearchParams,
    performSearch,
    results,
    loading,
    error,
  } = useSearch();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showRangeSearch, setShowRangeSearch] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [bedroomCount, setBedroomCount] = useState("");
  const [bathroomCount, setBathroomCount] = useState("");

  const mobileMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle scroll effect for header styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle body scroll locking when mobile menu or search is open
  useEffect(() => {
    if (mobileMenuOpen || searchOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [mobileMenuOpen, searchOpen]);

  // Reset menu state on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    closeSearch();
  }, [router.asPath]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close account menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    }

    if (accountMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [accountMenuOpen]);

  const isActive = (path) => {
    if (path === "/") {
      return router.pathname === "/";
    }
    return router.pathname.startsWith(path);
  };

  const navigationItems = [
    { name: "Home", path: "/", icon: <FiHome className="mr-3 h-5 w-5" aria-hidden="true" /> },
    { name: "Properties", path: "/listings", icon: <FiList className="mr-3 h-5 w-5" aria-hidden="true" /> },
    { name: "Shortlets", path: "/shortlets", icon: <FiMapPin className="mr-3 h-5 w-5" aria-hidden="true" /> },
    { name: "Contact", path: "/contact", icon: <FiPhone className="mr-3 h-5 w-5" aria-hidden="true" /> },
  ];

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const searchData = {
      query: searchQuery.trim(),
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
      bedrooms: bedroomCount,
      bathrooms: bathroomCount,
    };

    // Update search params and perform search
    updateSearchParams(searchData);
    performSearch(searchData);
    setShowResults(true);
    setShowRangeSearch(false);
  };

  // Handle viewing all results
  const handleViewAllResults = () => {
    const queryParams = new URLSearchParams();

    if (searchQuery.trim()) {
      queryParams.set("q", searchQuery.trim());
    }

    if (priceRange.min) queryParams.set("minPrice", priceRange.min);
    if (priceRange.max) queryParams.set("maxPrice", priceRange.max);
    if (bedroomCount) queryParams.set("bedrooms", bedroomCount);
    if (bathroomCount) queryParams.set("bathrooms", bathroomCount);

    // Redirect to listings page with search parameters
    router.push(`/listings?${queryParams.toString()}`);
    setSearchOpen(false);
  };

  // Reset search when closing modal
  const closeSearch = () => {
    setSearchOpen(false);
    setShowResults(false);
    setSearchQuery("");
    setPriceRange({ min: "", max: "" });
    setBedroomCount("");
    setBathroomCount("");
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg py-3" : "bg-white py-5"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-8">
        <nav className="relative flex items-center justify-between">
          {/* Logo with gold accent */}
          <div className="flex-shrink-0 z-10">
            <Link href="/" className="flex items-center">
              <div className="relative">
                <Image
                  src="/logo.svg"
                  alt="Topdial"
                  width={150}
                  height={52}
                  className={`transition-all duration-300 ${scrolled ? "scale-[0.87]" : ""}`}
                  priority
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:justify-center lg:space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                href={item.path}
                className={`relative px-3 py-2 text-sm font-medium transition-all duration-200 
                  ${isActive(item.path) ? "text-slate-900" : "text-slate-600 hover:text-slate-900"} uppercase tracking-wider`}
              >
                {item.name}
                {isActive(item.path) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-amber-400 to-amber-300" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {/* Search button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900 rounded-full transition-colors flex items-center"
              aria-label="Search"
            >
              <FiSearch className="w-5 h-5" />
            </button>

            {/* Authentication */}
            {isLoaded && (
              <>
                {isSignedIn ? (
                  <div className="relative ml-3" ref={accountMenuRef}>
                    <div>
                      <button
                        onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                        className="flex items-center text-slate-700 hover:text-slate-900 px-4 py-2 rounded-full bg-slate-50 hover:bg-slate-100 text-sm font-medium gap-x-1 transition-all duration-200 border border-slate-100"
                        aria-expanded={accountMenuOpen}
                      >
                        <span className="font-medium">Dashboard</span>
                        <FiChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            accountMenuOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {/* Account dropdown menu */}
                    {accountMenuOpen && (
                      <div
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl ring-1 ring-black/5 py-1 z-50 overflow-hidden border border-slate-100"
                        role="menu"
                      >
                        <div className="p-3 bg-slate-50 border-b border-slate-100">
                          <div className="flex items-center">
                            <UserButton afterSignOutUrl="/" />
                            <div className="ml-3">
                              <p className="text-xs text-slate-500">Logged in as</p>
                              <p className="text-sm font-medium text-slate-700">Account</p>
                            </div>
                          </div>
                        </div>

                        <div className="py-1">
                          {isAdmin && (
                            <Link
                              href="/dashboard/admin"
                              className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                              role="menuitem"
                            >
                              Admin Dashboard
                            </Link>
                          )}
                          {isAgent && (
                            <Link
                              href="/dashboard/agent"
                              className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                              role="menuitem"
                            >
                              Agent Dashboard
                            </Link>
                          )}
                          <Link
                            href="/dashboard"
                            className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                            role="menuitem"
                          >
                            <FiUser className="mr-2 h-4 w-4 text-slate-500" />
                            My Dashboard
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Link
                      href="/auth/sign-in"
                      className="flex items-center px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-all duration-200"
                    >
                      <FiLogIn className="mr-2 h-4 w-4" />
                      Sign in
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-full transition-all duration-200"
                    >
                      <FiUserPlus className="mr-2 h-4 w-4" />
                      Register
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile header actions */}
          <div className="flex items-center space-x-2 lg:hidden">
            {/* Mobile search button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 text-slate-600 hover:text-slate-900 rounded-full transition-colors"
              aria-label="Search"
            >
              <FiSearch className="w-5 h-5" />
            </button>

            {/* Mobile menu button */}
            <button
              id="mobile-menu-button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-600 hover:text-slate-900 rounded-full transition-colors"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
            >
              <FiMenu className="w-6 h-6" />
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile menu - Slide-in panel */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center">
            <Image src="/logo.svg" alt="Topdial" width={100} height={35} />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-slate-600 hover:text-slate-900 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1 pb-4">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                href={item.path}
                className={`flex items-center py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? "bg-slate-50 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.icon}
                {item.name}
                {isActive(item.path) && (
                  <div className="ml-auto">
                    <FiChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Authentication in mobile menu */}
          <div className="pt-4 border-t border-slate-100">
            <p className="px-3 text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Account</p>

            {isLoaded && (
              <>
                {isSignedIn ? (
                  <div className="space-y-1">
                    <Link
                      href="/messages"
                      className="flex items-center py-3 px-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiMessageSquare className="mr-3 h-5 w-5" />
                      My Messages
                    </Link>
                    
                    <Link
                      href="/dashboard"
                      className="flex items-center py-3 px-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiUser className="mr-3 h-5 w-5" />
                      My Dashboard
                    </Link>

                    {isAgent && (
                      <Link
                        href="/dashboard/agent"
                        className="flex items-center py-3 px-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <FiList className="mr-3 h-5 w-5" />
                        Agent Dashboard
                      </Link>
                    )}

                    {isAdmin && (
                      <Link
                        href="/dashboard/admin"
                        className="flex items-center py-3 px-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <FiSettings className="mr-3 h-5 w-5" />
                        Admin Dashboard
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 p-3">
                    <Link
                      href="/auth/sign-in"
                      className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:border-slate-300 hover:text-slate-900 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiLogIn className="mr-2 h-4 w-4" />
                      Sign in
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiUserPlus className="mr-2 h-4 w-4" />
                      Register
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-white/60 backdrop-blur-sm z-40 lg:hidden"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Search Overlay */}
      {searchOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 p-2 sm:p-4 flex items-start justify-center pt-[8vh] sm:pt-[5vh]"
            onClick={closeSearch}
          >
            <div
              className={`w-full ${showResults ? 'max-w-5xl' : 'max-w-3xl'} bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 max-h-[85vh] flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleSearchSubmit} className="p-3 sm:p-6 flex-shrink-0">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Search Properties</h2>
                  <button
                    type="button"
                    onClick={closeSearch}
                    className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative mb-4 sm:mb-6">
                  <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 z-10" />
                    <input
                      ref={searchInputRef}
                      type="search"
                      placeholder="Search properties, locations, or features..."
                      className="w-full pl-12 pr-12 py-3 sm:py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-rose-700 focus:border-rose-700 outline-none transition-all text-base sm:text-lg text-slate-900 placeholder:text-slate-400 bg-slate-50 focus:bg-white"
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowRangeSearch(!showRangeSearch)}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-colors ${
                        showRangeSearch 
                          ? "bg-rose-100 text-rose-700" 
                          : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                      }`}
                      aria-label="Advanced search"
                    >
                      <FiSliders className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Advanced Search Options */}
                {showRangeSearch && (
                  <div className="mb-4 sm:mb-6 border rounded-xl border-slate-200 p-4 sm:p-6 bg-slate-50">
                    <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center">
                      <FiSliders className="w-4 h-4 mr-2" />
                      Advanced Search Options
                    </h3>

                    <div className="space-y-4">
                      {/* Price Range */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Min Price (₦)</label>
                          <input
                            type="number"
                            placeholder="e.g. 500000"
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-700 focus:border-rose-700 transition-colors bg-white"
                            value={priceRange.min}
                            onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Max Price (₦)</label>
                          <input
                            type="number"
                            placeholder="e.g. 5000000"
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-700 focus:border-rose-700 transition-colors bg-white"
                            value={priceRange.max}
                            onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* Rooms */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Bedrooms</label>
                          <select
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-700 focus:border-rose-700 transition-colors bg-white"
                            value={bedroomCount}
                            onChange={(e) => setBedroomCount(e.target.value)}
                          >
                            <option value="">Any bedrooms</option>
                            <option value="1">1+ bedroom</option>
                            <option value="2">2+ bedrooms</option>
                            <option value="3">3+ bedrooms</option>
                            <option value="4">4+ bedrooms</option>
                            <option value="5">5+ bedrooms</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Bathrooms</label>
                          <select
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-700 focus:border-rose-700 transition-colors bg-white"
                            value={bathroomCount}
                            onChange={(e) => setBathroomCount(e.target.value)}
                          >
                            <option value="">Any bathrooms</option>
                            <option value="1">1+ bathroom</option>
                            <option value="2">2+ bathrooms</option>
                            <option value="3">3+ bathrooms</option>
                            <option value="4">4+ bathrooms</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Search Links */}
                {!showRangeSearch && !showResults && (
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-sm font-medium text-slate-500 mb-3">Popular searches</h3>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      <Link
                        href="/listings?listingType=sale&location=Lagos"
                        className="inline-flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 text-sm font-medium transition-colors"
                        onClick={closeSearch}
                      >
                        <FiMapPin className="mr-2 h-4 w-4" />
                        Lagos properties
                      </Link>
                      <Link
                        href="/listings?listingType=rent"
                        className="inline-flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 text-sm font-medium transition-colors"
                        onClick={closeSearch}
                      >
                        <FiHome className="mr-2 h-4 w-4" />
                        Rental homes
                      </Link>
                      <Link
                        href="/listings?category=luxury"
                        className="inline-flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 text-sm font-medium transition-colors"
                        onClick={closeSearch}
                      >
                        <FiHeart className="mr-2 h-4 w-4" />
                        Luxury homes
                      </Link>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {!showResults ? (
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-rose-800 hover:bg-rose-900 disabled:bg-rose-600 text-white rounded-xl py-3 sm:py-4 font-semibold transition-colors flex items-center justify-center text-base"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Searching...
                        </>
                      ) : (
                        <>
                          <FiSearch className="w-5 h-5 mr-3" />
                          Search Properties
                        </>
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-600 text-white rounded-xl py-3 sm:py-4 font-semibold transition-colors flex items-center justify-center text-base"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Searching...
                          </>
                        ) : (
                          <>
                            <FiSearch className="w-5 h-5 mr-3" />
                            Search Again
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleViewAllResults}
                        className="flex-1 bg-rose-800 hover:bg-rose-900 text-white rounded-xl py-3 sm:py-4 font-semibold transition-colors flex items-center justify-center text-base"
                      >
                        <FiList className="w-5 h-5 mr-3" />
                        View All Results
                      </button>
                    </>
                  )}
                </div>
              </form>

              {/* Search Results */}
              {showResults && (
                <div className="border-t border-slate-200 flex-1 overflow-hidden">
                  {loading ? (
                    <div className="p-6 sm:p-8 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rose-800 mx-auto mb-4"></div>
                      <p className="text-slate-600 font-medium">Searching properties...</p>
                      <p className="text-slate-400 text-sm mt-1">This might take a moment</p>
                    </div>
                  ) : error ? (
                    <div className="p-6 sm:p-8 text-center">
                      <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiX className="w-8 h-8 text-rose-700" />
                      </div>
                      <p className="text-rose-700 font-semibold mb-2">Error searching properties</p>
                      <p className="text-slate-500 text-sm">{error}</p>
                    </div>
                  ) : results && results.length > 0 ? (
                    <div className="overflow-y-auto flex-1">
                      <div className="p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-slate-600">
                            Found <span className="font-semibold text-slate-900">{results.length}</span> propert{results.length === 1 ? 'y' : 'ies'}
                          </p>
                          {results.length > 5 && (
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                              Showing top 5
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          {results.slice(0, 5).map((property) => (
                            <Link
                              key={property._id}
                              href={`/listings/${property._id}`}
                              onClick={closeSearch}
                              className="block p-4 border border-slate-200 rounded-xl hover:border-rose-300 hover:shadow-md transition-all duration-200 group"
                            >
                              <div className="flex gap-4">
                                {property.primaryImage || (property.images && property.images.length > 0) ? (
                                  <div className="relative flex-shrink-0">
                                    <Image
                                      src={property.primaryImage || property.images[0]?.url || property.images[0]}
                                      alt={property.title}
                                      width={80}
                                      height={80}
                                      className="rounded-lg object-cover w-20 h-20 sm:w-24 sm:h-24"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg"></div>
                                  </div>
                                ) : (
                                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-slate-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                                    <FiHome className="w-8 h-8 text-slate-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-slate-900 truncate group-hover:text-rose-800 transition-colors">
                                    {property.title}
                                  </h4>
                                  <p className="text-sm text-slate-600 truncate mt-1 flex items-center">
                                    <FiMapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                                    {property.location}
                                  </p>
                                  <p className="text-base font-bold text-slate-900 mt-2">
                                    ₦{property.price?.toLocaleString() || 'Price on request'}
                                  </p>
                                </div>
                                <div className="flex-shrink-0 flex items-center">
                                  <FiChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rose-700 transition-colors" />
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                        
                        {results.length > 5 && (
                          <div className="mt-6 text-center">
                            <button
                              onClick={handleViewAllResults}
                              className="inline-flex items-center px-6 py-3 bg-rose-50 hover:bg-rose-100 text-rose-800 font-semibold rounded-xl transition-colors"
                            >
                              <FiList className="w-5 h-5 mr-2" />
                              View all {results.length} properties
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 sm:p-8 text-center">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiSearch className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-600 font-semibold mb-2">No properties found</p>
                      <p className="text-slate-500 text-sm mb-4">Try adjusting your search criteria or browse our popular searches</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Link
                          href="/listings"
                          onClick={closeSearch}
                          className="inline-flex items-center px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors text-sm"
                        >
                          <FiList className="w-4 h-4 mr-2" />
                          Browse all properties
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
